import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Alert,
  Box,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  COLOR_AIRBTICS_PRO_CTA_BUTTON,
  COLOR_GREY_200,
  COLOR_GREY_400,
  COLOR_GREY_600,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_SUCCESS,
  COLOR_TEXT_2,
} from 'src/colors';
import {
  COLOR_GREEN,
  COLOR_PRIMARY_DARK,
  COLOR_RED,
  COLOR_WHITE,
  CountryCodeWithCurrency,
  airbticsProCountries,
  countryWiseAirbticsProImgLinkObj,
  countryWiseAirbticsProPaymentLinkObj,
  countryWiseAirbticsProTableLinks,
  exchangeRates,
} from 'src/const';
import ToggleSwitch from '../Components/Buttons/ToggleSwitch';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { OutlinedRectangularButton } from '../Components/Buttons';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
  convertCurrency,
  convertToUSD,
  csvToDataGridObjectArray,
  extractNumericValue,
  formatCurrency,
  getColorFromSizeLevel,
  sortFormattedStringByNumber,
} from 'src/utils/functions';
import { callApi } from 'src/core/callApi';

const DiscoveryTableRegulationMap = ['Lenient', 'Medium', 'Strict'];

const AirbticsProDiscovery = ({
  currency,
  setCurrency,
  airbticsProCountryCode,
  isMdScreenSize,
}) => {
  const [airbnbStatisticsData, setAirbnbStatisticsData] = useState<
    { [key: string]: number | string }[]
  >([]);
  const [realEstateAnalysisTableData, setRealEstateAnalysisTableData] = useState<
    { [key: string]: number | string }[]
  >([]);
  const [tableType, setTableType] = useState('Airbnb statistics');
  const [airbticsProCTAImage, setAirbticsProCTAImage] = useState<string>();

  useEffect(() => {
    const newCurrency = airbticsProCountryCode
      ? CountryCodeWithCurrency[airbticsProCountryCode]
      : 'USD';
    console.log('New Currency:: ', newCurrency);
    setCurrency(newCurrency);
  }, [window.google]);

  const fetchAirbticsProTableData = async (
    URL: string,
    setData: React.Dispatch<React.SetStateAction<{ [key: string]: string | number }[]>>,
    blurDataCount: number = 0
  ) => {
    const csvData = await callApi(URL, null, false);
    let dataArray = await csvToDataGridObjectArray(csvData);

    for (let idx = 1; idx <= blurDataCount; idx++) {
      dataArray.push(dataArray[(idx - 1) % (dataArray.length - 1)]);
    }

    setData(_ => dataArray);
  };

  const getCountryWiseFiles = (countryCode: string, fileType: 'csv' | 'image' | 'paymentLink') => {
    if (fileType === 'image') {
      return countryWiseAirbticsProImgLinkObj[countryCode];
    }

    if (fileType === 'paymentLink') {
      return countryWiseAirbticsProPaymentLinkObj[countryCode];
    }

    //CSV
    return countryWiseAirbticsProTableLinks[countryCode];
  };

  const findAirbticsProCountryCode = (countryCode: string) => {
    let resultCountry = '';
    Object.keys(airbticsProCountries).forEach(country => {
      if (airbticsProCountries[country] === countryCode) {
        resultCountry = country;
      }
    });
    return resultCountry;
  };

  const fetchAllAirbticsProTableData = (airbticsProCountryCode: string) => {
    fetchAirbticsProTableData(
      getCountryWiseFiles(airbticsProCountryCode, 'csv').statistics_data,
      setAirbnbStatisticsData
    );
    fetchAirbticsProTableData(
      getCountryWiseFiles(airbticsProCountryCode, 'csv').real_estate_analysis_data,
      setRealEstateAnalysisTableData,
      5
    );
  };

  useEffect(() => {
    setAirbticsProCTAImage(getCountryWiseFiles(airbticsProCountryCode, 'image'));
    airbticsProCountryCode && fetchAllAirbticsProTableData(airbticsProCountryCode);
  }, [airbticsProCountryCode]);

  const columnsAirbnbStatisticsTable: GridColDef[] = [
    {
      field: 'Links',
      headerName: 'Links',
      hide: true,
    },
    {
      // field: 'cityName',
      field: 'City Name',
      headerName: 'City Name',
      flex: 2,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Typography
            variant="body1"
            sx={{
              color: COLOR_PRIMARY_1,
              fontWeight: 'bold',
            }}
            onClick={() => window.open(params.row.Links)}
          >
            {params.value} <OpenInNewRoundedIcon sx={{ fontSize: '16px' }} />
          </Typography>
        );
      },
    },
    {
      field: 'Regulation',
      headerName: 'Regulation',
      flex: 1,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        if (params.value) {
          return (
            <Box
              sx={{
                width: '100px',
                height: '30px',
                borderRadius: '30px',
                background: getColorFromSizeLevel(
                  DiscoveryTableRegulationMap.indexOf(params.value)
                ),
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  textAlign: 'center',
                  color: COLOR_WHITE,
                }}
              >
                {params.value}
              </Typography>
            </Box>
          );
        } else return '';
      },
    },
    {
      field: 'Airbnb Listings',
      headerName: 'Number of active Airbnb listings',
      flex: 1.5,
      sortComparator: sortFormattedStringByNumber,
      minWidth: 100,
    },
    {
      field: 'Non-Airbnb Listings',
      headerName: 'Number of active Non Airbnb listings',
      flex: 1.5,
      sortComparator: sortFormattedStringByNumber,
      minWidth: 100,
    },
    {
      field: 'Total STR Listings',
      headerName: 'Total STR Listings',
      flex: 1.5,
      sortComparator: sortFormattedStringByNumber,
      minWidth: 100,
    },
    {
      field: 'Occupancy Rate (LTM)',
      headerName: 'Occupancy Rate (LTM)',
      flex: 1.5,
      sortComparator: sortFormattedStringByNumber,
      minWidth: 100,
    },
    {
      field: 'YoY Occupancy Change',
      headerName: 'YoY occupancy change (LTM vs Previous LTM)',
      flex: 1.5,
      sortComparator: sortFormattedStringByNumber,
      minWidth: 100,
      renderCell: params => {
        return (
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              textAlign: 'center',
              color: extractNumericValue(params.value) < 0 ? COLOR_RED : COLOR_GREEN,
            }}
          >
            {params.value}
          </Typography>
        );
      },
    },
    {
      field: 'Cap Rate',
      headerName: 'Cap Rate',
      flex: 1.5,
      sortComparator: sortFormattedStringByNumber,
      minWidth: 100,
    },
    {
      field: 'Annual Revenue',
      headerName: 'Potential Annual Revenue',
      flex: 1.5,
      sortComparator: sortFormattedStringByNumber,
      minWidth: 100,
      valueFormatter: params => {
        const parsedNumberValue = extractNumericValue(params.value);
        const parsedNumberValueUSD = convertToUSD(
          CountryCodeWithCurrency[airbticsProCountryCode],
          parsedNumberValue
        );
        const formattedValue = formatCurrency(
          convertCurrency(currency, parsedNumberValueUSD).toLocaleString()
        );
        return `${exchangeRates[currency].symbol}${formattedValue}`;
      },
    },
    {
      field: 'Med Property Price',
      headerName: 'Median Property Price',
      flex: 2,
      sortComparator: sortFormattedStringByNumber,
      minWidth: 100,
      valueFormatter: params => {
        const parsedNumberValue = extractNumericValue(params.value);
        const parsedNumberValueUSD = convertToUSD(
          CountryCodeWithCurrency[airbticsProCountryCode],
          parsedNumberValue
        );
        const formattedValue = formatCurrency(
          convertCurrency(currency, parsedNumberValueUSD).toLocaleString()
        );
        return `${exchangeRates[currency].symbol}${formattedValue}`;
      },
    },
  ];

  const columnsRealEstateAnalysisTable: GridColDef[] = [
    {
      field: 'City Name',
      headerName: 'City Name',
      flex: 2,
      sortComparator: undefined,
      minWidth: 100,
    },
    {
      field: 'Population',
      headerName: 'Population',
      flex: 1,
      sortComparator: undefined,
      minWidth: 100,
    },
    {
      field: 'Property Price',
      headerName: 'Property Price',
      flex: 1.5,
      sortComparator: undefined,
      minWidth: 100,
    },
    {
      field: 'Income Level',
      headerName: 'Income Level',
      flex: 1.5,
      sortComparator: undefined,
      minWidth: 100,
    },
    {
      field: 'Google Travel Related Search',
      headerName: 'Google Travel Related Search',
      flex: 1.5,
      sortComparator: undefined,
      minWidth: 100,
    },
    {
      field: 'Crime',
      headerName: 'Crime',
      flex: 1.5,
      sortComparator: undefined,
      minWidth: 100,
    },
    {
      field: 'Unemployment',
      headerName: 'Unemployment',
      flex: 1.5,
      sortComparator: undefined,
      minWidth: 100,
    },
  ];

  const generateBulletPointList = (text: string, iconCenter: boolean = false) => {
    return (
      <ListItem alignItems={iconCenter ? 'center' : 'flex-start'}>
        <ListItemIcon>
          <CheckCircleRoundedIcon sx={{ color: COLOR_SUCCESS }} />
        </ListItemIcon>
        <ListItemText primary={text} secondary={null} />
      </ListItem>
    );
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Find the highest demand Airbnb markets around your area</title>
      </Helmet>

      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{ minHeight: '80vh', padding: isMdScreenSize ? '20px' : '40px' }}
      >
        <Grid item xs={12}>
          <Grid>
            <Typography
              variant="h5"
              sx={{ fontWeight: 'bold', color: COLOR_TEXT_2, marginBottom: '10px' }}
            >
              Looking for the best places to own an Airbnb in{' '}
              {findAirbticsProCountryCode(airbticsProCountryCode)}{' '}
              {
                <img
                  loading="lazy"
                  width="40"
                  src={`https://flagcdn.com/${
                    airbticsProCountries[airbticsProCountryCode]?.toLowerCase() === 'uk'
                      ? 'gb'
                      : airbticsProCountries[airbticsProCountryCode]?.toLowerCase()
                  }.svg`}
                  alt=""
                />
              }{' '}
              ?
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'normal', color: COLOR_GREY_600, marginBottom: '20px' }}
            >
              Several measurable factors can influence the financial performance of an Airbnb
              market. Analyzing these aspects leads to a data-driven investment approach that can
              assist UK investors in identifying the most profitable Airbnb locations.
            </Typography>
            <Typography variant="h1">
              <ToggleSwitch
                values={['Airbnb statistics', 'Real estate analysis']}
                type={tableType}
                setType={setTableType}
                float="left"
                size="large"
              />
            </Typography>
          </Grid>
        </Grid>

        {realEstateAnalysisTableData && tableType === 'Real estate analysis' && (
          <Grid item xs={12}>
            <Alert severity="info" sx={{ boxShadow: '0 2px 4px -2px #444' }}>
              All data is 5 years average YoY change.
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          {airbnbStatisticsData && tableType === 'Airbnb statistics' && (
            <DataGrid
              rows={airbnbStatisticsData}
              columns={columnsAirbnbStatisticsTable}
              checkboxSelection={false}
              disableSelectionOnClick
              hideFooter={true}
              // disableColumnMenu={true}
              sx={{
                height: '500px',
                background: COLOR_WHITE,
                borderRadius: '20px',
                padding: '10px 20px',
                '& .MuiDataGrid-iconSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: COLOR_PRIMARY_DARK,
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-cellContent': {
                  fontWeight: 500,
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#eee', //rgba(167, 113, 255)
                  cursor: 'pointer',
                },
              }}
            />
          )}
        </Grid>

        {realEstateAnalysisTableData && tableType === 'Real estate analysis' && (
          <Grid xs={12} sx={{ marginBottom: '100px' }}>
            {' '}
            <DataGrid
              rows={realEstateAnalysisTableData}
              columns={columnsRealEstateAnalysisTable}
              checkboxSelection={false}
              disableSelectionOnClick
              hideFooter={true}
              disableColumnMenu={true}
              sx={{
                height: '500px',
                background: COLOR_WHITE,
                borderRadius: '20px',
                padding: '10px 20px',
                '& .MuiDataGrid-iconSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: COLOR_PRIMARY_DARK,
                  fontWeight: 'bold',
                },
                '& .MuiDataGrid-cellContent': {
                  fontWeight: 500,
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#eee', //rgba(167, 113, 255)
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-row:nth-last-child(-n+5)': {
                  filter: 'blur(5px)',
                  pointerEvents: 'none',
                },
              }}
            />
            <Grid
              xs={12}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
                marginTop: '-150px',
              }}
            >
              <OutlinedRectangularButton
                height="40px"
                width="min(max(300px, 60%), 460px)"
                colors={{
                  backgroundColor: COLOR_PRIMARY_2,
                  color: '#fff',
                  borderColor: COLOR_PRIMARY_2,
                }}
                text={`Get Expert Airbnb Profit Analysis for 50 ${findAirbticsProCountryCode(
                  airbticsProCountryCode
                )} Markets!`}
                icon={<LockIcon sx={{ fontSize: 'inherit', marginRight: '6px' }} />}
                handleOnClick={() => {
                  window.open(countryWiseAirbticsProPaymentLinkObj[airbticsProCountryCode]);
                }}
              />
            </Grid>
          </Grid>
        )}
      </Grid>

      <Grid
        item
        container
        xs={12}
        style={{ maxWidth: '900px', marginTop: '60px', padding: '20px' }}
      >
        <Grid
          container
          item
          xs={12}
          sm={6}
          justifyContent={'center'}
          alignItems={'center'}
          spacing={2}
          sx={{ marginBottom: '40px' }}
        >
          <img src={airbticsProCTAImage} style={{ width: '80%' }} />
        </Grid>
        <Grid
          container
          item
          xs={12}
          sm={6}
          spacing={1}
          direction={'column'}
          justifyContent={'flex-start'}
          alignItems={'flex-start'}
        >
          <Box
            sx={{
              padding: '10px 15px',
              borderRadius: 900,
              fontSize: '20px',
              backgroundColor: COLOR_GREY_200,
              marginBottom: '20px',
            }}
          >
            <Typography variant="body1" fontWeight={'medium'}>
              Pro Book
            </Typography>
          </Box>

          <Typography
            variant="h5"
            fontWeight="bold"
            sx={{
              '& .text-highlight': {
                color: COLOR_PRIMARY_1,
              },
              fontSize: '32px',
            }}
          >
            Deeper <span className="text-highlight">STR</span> Insights.{' '}
            <span className="text-highlight">Broader Investment</span> Opportunities.
          </Typography>

          <Typography variant="body1" color={COLOR_TEXT_2} sx={{ marginTop: '20px' }}>
            Want to go deeper, broader, and faster? Supercharge your Airbnb Investment by accessing
            the most in-depth Airbnb & real estate insights with our exclusive STR report.
          </Typography>

          <List>
            {generateBulletPointList(
              'Vital vacation rental data from 40 Markets – Airbnb Occupancy Rate, ADR, Revenue, and Listings! Access 2-year data from June 2021 to June 2023.'
            )}
            {generateBulletPointList(
              'Uncover 5-year real estate investability metrics for 40 markets including population, property prices, income levels, Google travel interest, crime, and unemployment trends 2017 - 2021)!'
            )}
            {generateBulletPointList('All yours in 3 business days!', true)}
          </List>

          <Grid item container direction={'row'}>
            <Grid item style={{ paddingLeft: 0, marginRight: '10px' }}>
              <OutlinedRectangularButton
                height="60px"
                width="120px"
                colors={{
                  backgroundColor: COLOR_AIRBTICS_PRO_CTA_BUTTON,
                  color: '#fff',
                  borderColor: COLOR_AIRBTICS_PRO_CTA_BUTTON,
                }}
                fontSize={'16px'}
                text={`Purchase`}
                handleOnClick={() => {
                  window.open(countryWiseAirbticsProPaymentLinkObj[airbticsProCountryCode]);
                }}
              />
            </Grid>
            <Grid item style={{ paddingLeft: 0 }}>
              <OutlinedRectangularButton
                height="60px"
                width="180px"
                colors={{
                  backgroundColor: '#fff',
                  color: COLOR_AIRBTICS_PRO_CTA_BUTTON,
                  borderColor: COLOR_AIRBTICS_PRO_CTA_BUTTON,
                }}
                fontSize={'16px'}
                text={'Download Sample'}
                handleOnClick={() =>
                  window.open(
                    'https://docs.google.com/spreadsheets/d/1oYI9ZP5aCcygQ6fqRXWZ1m_hjhLsLdNFaVYRCSE2JJA/edit#gid=947917615 ',
                    'nofollow'
                  )
                }
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default AirbticsProDiscovery;
